import { useSelector } from "react-redux";
import {
  FAVOURITES_VIEW,
  MARKETS_VIEW,
  ODDS_VIEW,
  FAVOURITES_LIMIT,
  favouritesSupportedViews,
  AnalyticsTypeMap,
} from "../constants";
import { sendMessage, sendAnalyticsEvent } from "../lib/postMessage";
import { generateFavouriteName } from "../lib/string";
import { generateFavouriteId } from "../lib/favourites";
import { ADD, FAVOURITES } from "../constants/analytics";

export const useFavourites = ({
  activeFilterView,
  loggedIn,
  favouritesEnabled,
}) => {
  const { prefetchedFilters, storedFavourites, filters } = useSelector(
    (state) => state.filter
  );

  const showAddToFavourites =
    loggedIn &&
    favouritesEnabled &&
    favouritesSupportedViews.includes(activeFilterView);

  const isFavouritesView = activeFilterView === FAVOURITES_VIEW;
  const isMarketsView = activeFilterView === MARKETS_VIEW;

  const hasStoredFavourites =
    storedFavourites.length > 0 &&
    storedFavourites.some((favourite) => favourite.type === MARKETS_VIEW) &&
    isMarketsView;

  const shouldFavouriteBeDisabled = () => {
    if (storedFavourites.length >= FAVOURITES_LIMIT) {
      return true;
    }

    if (
      activeFilterView === ODDS_VIEW &&
      // if no minOdds or maxOdds are set
      ((!prefetchedFilters?.odds?.minOdds &&
        !prefetchedFilters?.odds?.maxOdds) ||
        // if the user has already added the combination of minOdds and maxOdds
        (storedFavourites.length > 0 &&
          storedFavourites.some(
            (favourite) =>
              favourite?.odds?.minOdds === prefetchedFilters?.odds?.minOdds &&
              favourite?.odds?.maxOdds === prefetchedFilters?.odds?.maxOdds
          )))
    ) {
      return true;
    }

    if (
      activeFilterView === MARKETS_VIEW &&
      !prefetchedFilters.markets.length
    ) {
      return true;
    }

    return false;
  };

  const storeNewFavourite = () => {
    sendMessage({
      type: "UpdateFavourites",
      payload: [
        ...storedFavourites,
        {
          id: generateFavouriteId(storedFavourites),
          name: generateFavouriteName(activeFilterView, storedFavourites),
          type: activeFilterView,
          [activeFilterView]: prefetchedFilters[activeFilterView],
        },
      ],
    });

    sendAnalyticsEvent({
      event: FAVOURITES,
      type:
        activeFilterView === ODDS_VIEW
          ? AnalyticsTypeMap.get(activeFilterView)
          : activeFilterView,
      action: ADD,
    });
  };

  const shouldShowFavouritesLimitMessage = () => {
    return Boolean(
      (showAddToFavourites ||
        (favouritesEnabled && activeFilterView === FAVOURITES_VIEW)) &&
        storedFavourites.length >= FAVOURITES_LIMIT - 1
    );
  };

  const getShowAllSelectionsDisabledState = () => {
    if (isFavouritesView) {
      return (
        storedFavourites.length === 0 ||
        (!prefetchedFilters.favourites.markets &&
          prefetchedFilters.favourites.markets === filters.favourites.markets &&
          !prefetchedFilters.favourites.odds &&
          prefetchedFilters.favourites.odds === filters.favourites.odds)
      );
    }
    return false; // This will be overridden by the parent component
  };

  return {
    showAddToFavourites,
    isFavouritesView,
    hasStoredFavourites,
    shouldFavouriteBeDisabled,
    storeNewFavourite,
    shouldShowFavouritesLimitMessage,
    getShowAllSelectionsDisabledState,
  };
};
